/**
 * Per-stage STATUS columns (owner 2026-10-25).
 *
 * One column per company stage that has statuses, labelled `{Stage name} status`.
 * A column exists for every ACTIVE stage with at least one ACTIVE status, in the
 * stage's `sortOrder` (then id), so columns come and go with the masters.
 *
 * ⚠️ Everything here is matched by ID (`status.stage.id`, `company.stage.id`),
 * never by name: status names repeat across stages and every name is
 * admin-editable. Names are used only for labels and as a sort tie-break.
 *
 * Column ids are `stageStatus:<stageId>` and are stored in saved layouts —
 * never rename the prefix. The legacy single column id `stageStatus` survives
 * only as a saved-layout alias ({@link legacyHiddenStageStatusIds}).
 *
 * Filtering: every column edits a slice of ONE shared `stageStatusId` list.
 * Selections across columns combine as a union (a company is in one stage only).
 */

import { useMemo } from 'react';
import type { CompanyStageStatus, CompanyStageStatusRef, ReferenceRecord } from './types';
import { companyStageLabel, useCompanyStages } from './use-company-stages';
import { useCompanyStageStatuses } from './use-company-stage-statuses';

export const STAGE_STATUS_COLUMN_PREFIX = 'stageStatus:';
export const LEGACY_STAGE_STATUS_COLUMN_ID = 'stageStatus';

export interface StageStatusColumn {
  /** `stageStatus:${stageId}` — stored in saved layouts. */
  id: string;
  stageId: number;
  stageName: string;
  /** `${stageName} status` */
  label: string;
  /** ACTIVE statuses of this stage, by sortOrder, then name, then id. */
  statuses: CompanyStageStatus[];
}

export function stageStatusColumnId(stageId: number): string {
  return `${STAGE_STATUS_COLUMN_PREFIX}${stageId}`;
}

/** The stage id of a `stageStatus:<id>` column id; `null` for anything else. */
export function parseStageStatusColumnId(id: string): number | null {
  if (!id.startsWith(STAGE_STATUS_COLUMN_PREFIX)) return null;
  const rest = id.slice(STAGE_STATUS_COLUMN_PREFIX.length);
  if (!/^[1-9]\d*$/.test(rest)) return null;
  const value = Number(rest);
  return Number.isSafeInteger(value) ? value : null;
}

export function isStageStatusColumnId(id: string): boolean {
  return parseStageStatusColumnId(id) !== null;
}

function compareStatuses(a: CompanyStageStatus, b: CompanyStageStatus): number {
  if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
  const byName = a.name.localeCompare(b.name);
  if (byName !== 0) return byName;
  return a.id - b.id;
}

function compareStages(a: ReferenceRecord, b: ReferenceRecord): number {
  if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
  return a.id - b.id;
}

/**
 * Pure: one column per ACTIVE stage with at least one ACTIVE status, in stage
 * order. `onlyStageIds` (when given) keeps only those stages — a table that
 * holds some stages shows only their columns.
 */
export function buildStageStatusColumns(
  stages: readonly ReferenceRecord[],
  statuses: readonly CompanyStageStatus[],
  opts?: { onlyStageIds?: ReadonlySet<number> | null },
): StageStatusColumn[] {
  const only = opts?.onlyStageIds ?? null;
  const activeByStage = new Map<number, CompanyStageStatus[]>();
  for (const status of statuses) {
    if (!status.isActive) continue;
    const list = activeByStage.get(status.stage.id);
    if (list) list.push(status);
    else activeByStage.set(status.stage.id, [status]);
  }

  return stages
    .filter((stage) => stage.isActive)
    .filter((stage) => only === null || only.has(stage.id))
    .filter((stage) => (activeByStage.get(stage.id)?.length ?? 0) > 0)
    .slice()
    .sort(compareStages)
    .map((stage) => {
      const stageName = companyStageLabel(stage.id, stage.name);
      return {
        id: stageStatusColumnId(stage.id),
        stageId: stage.id,
        stageName,
        label: `${stageName} status`,
        statuses: (activeByStage.get(stage.id) ?? []).slice().sort(compareStatuses),
      };
    });
}

export type StageStatusCellState =
  /** The company is not in this column's stage (or has no stage): `—`, read-only. */
  | { kind: 'other-stage' }
  | { kind: 'value'; status: CompanyStageStatusRef | null; editable: false }
  | {
      kind: 'value';
      status: CompanyStageStatusRef | null;
      editable: true;
      /** The stage's active statuses, plus the company's current one if retired. */
      options: CompanyStageStatus[];
    };

/**
 * What one cell shows. Editable only in the column of the company's own stage,
 * and only when `canEdit`. The options are the column's active statuses plus
 * the company's current status when it is retired (so a stored value is never
 * silently dropped). The "no status" option is the renderer's job.
 */
export function stageStatusCellState(
  company: { stage?: { id: number } | null; stageStatus?: CompanyStageStatusRef | null },
  column: StageStatusColumn,
  allStatuses: readonly CompanyStageStatus[],
  canEdit: boolean,
): StageStatusCellState {
  const stageId = company.stage?.id;
  if (stageId === undefined || stageId === null || stageId !== column.stageId) {
    return { kind: 'other-stage' };
  }
  const status = company.stageStatus ?? null;
  if (!canEdit) return { kind: 'value', status, editable: false };

  const options = column.statuses.slice();
  if (status !== null && !options.some((option) => option.id === status.id)) {
    const master = allStatuses.find((row) => row.id === status.id);
    if (master) {
      // Retired (or otherwise absent from the column) but of THIS stage: keep it.
      if (master.stage.id === column.stageId) options.push(master);
    } else {
      // Not in the master list (not loaded yet, or failed): keep the stored value.
      options.push({
        id: status.id,
        name: status.name,
        sortOrder: Number.MAX_SAFE_INTEGER,
        isActive: false,
        stage: { id: column.stageId, name: column.stageName },
        followUpSlaDays: status.followUpSlaDays,
        resurfaceMode: status.resurfaceMode,
        excludesFollowUp: status.excludesFollowUp,
        requiresReason: status.requiresReason,
        suppressesMarketing: status.suppressesMarketing,
      });
    }
    options.sort(compareStatuses);
  }
  return { kind: 'value', status, editable: true, options };
}

/** Is `id` a status of `column`'s stage? Retired statuses count; unknown ids do not. */
function belongsToColumn(
  column: StageStatusColumn,
  id: number,
  stageOf: ReadonlyMap<number, number>,
): boolean {
  const stageId = stageOf.get(id);
  if (stageId !== undefined) return stageId === column.stageId;
  return column.statuses.some((status) => status.id === id);
}

function stageIndex(allStatuses: readonly CompanyStageStatus[]): Map<number, number> {
  const index = new Map<number, number>();
  for (const status of allStatuses) index.set(status.id, status.stage.id);
  return index;
}

/** The ids of the shared `stageStatusId` list that belong to `column`'s stage. */
export function columnSelection(
  column: StageStatusColumn,
  allSelected: readonly number[],
  allStatuses: readonly CompanyStageStatus[],
): number[] {
  const stageOf = stageIndex(allStatuses);
  const seen = new Set<number>();
  const out: number[] = [];
  for (const id of allSelected) {
    if (seen.has(id) || !belongsToColumn(column, id, stageOf)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/**
 * The shared `stageStatusId` list after `column`'s funnel picks `next`: ids of
 * other stages (and unknown ids) are kept in place; this stage's slice is
 * replaced by `next` (deduplicated, ids of other stages in `next` ignored).
 */
export function replaceColumnSelection(
  column: StageStatusColumn,
  allSelected: readonly number[],
  next: readonly number[],
  allStatuses: readonly CompanyStageStatus[],
): number[] {
  const stageOf = stageIndex(allStatuses);
  const seen = new Set<number>();
  const out: number[] = [];
  for (const id of allSelected) {
    if (seen.has(id) || belongsToColumn(column, id, stageOf)) continue;
    seen.add(id);
    out.push(id);
  }
  for (const id of next) {
    if (seen.has(id) || !belongsToColumn(column, id, stageOf)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/**
 * Saved-layout migration: a person whose layout hid the legacy `stageStatus`
 * column gets every per-stage column hidden. Pass as
 * `ColumnLayoutOptions.legacyHiddenAliases`.
 */
export function legacyHiddenStageStatusIds(
  columns: readonly StageStatusColumn[],
): Record<string, string[]> {
  return { [LEGACY_STAGE_STATUS_COLUMN_ID]: columns.map((column) => column.id) };
}

export interface StageStatusColumnsState {
  columns: StageStatusColumn[];
  /** Every status, active and retired, in the server's order. */
  statuses: CompanyStageStatus[];
  loading: boolean;
  /** Either master could not be read. */
  failed: boolean;
}

/**
 * The stage and stage-status masters, composed into columns. `enabled: false`
 * skips both requests (and reports `loading: false`). Memoise `onlyStageIds`
 * in the caller, or the columns are rebuilt every render.
 */
export function useStageStatusColumns(opts?: {
  onlyStageIds?: ReadonlySet<number> | null;
  enabled?: boolean;
}): StageStatusColumnsState {
  const enabled = opts?.enabled ?? true;
  const onlyStageIds = opts?.onlyStageIds ?? null;
  const stages = useCompanyStages(enabled);
  const statuses = useCompanyStageStatuses(enabled);

  const columns = useMemo(
    () => buildStageStatusColumns(stages.records, statuses.statuses, { onlyStageIds }),
    [stages.records, statuses.statuses, onlyStageIds],
  );

  return {
    columns,
    statuses: statuses.statuses,
    loading: stages.loading || statuses.loading,
    failed: stages.failed || statuses.error !== null,
  };
}
